import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import { ProductDescriptionSettingsDTO, PromptPlaceholdersDTO } from "@app/api/generated";
import { ClickableComponent, TextFieldComponent } from "@app/core";
import { ModalComponent } from "@app/core/modal";
import { SpinningLoader } from "@app/core/spinning-loader/spinning-loader";
import { getDescriptionSettings, saveDescriptionSettings } from "@app/api/product-description-api";

import styles from "@app/styles/dashboard/prompt-settings.module.scss";

interface IPromptSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IErrors {
  minWords?: string;
  maxWords?: string;
  prompt?: string;
}

const toInput = (value?: number | null): string => (value != null ? String(value) : "");

/**
 * The ESB sends the fixed prompt text with the two word counts as placeholders, so the read-only
 * blocks show the numbers being typed instead of the ones that were saved last time. Which tokens
 * those are comes from the same response (`promptPlaceholders`) — hard-coding them here would leave
 * a raw `{minWoorden}` on screen the day the ESB renames one.
 */
const fillWordCounts = (
  template: string,
  placeholders: PromptPlaceholdersDTO | undefined,
  minWords: string,
  maxWords: string
): string => {
  if (!placeholders) {
    return template;
  }
  return template.replace(placeholders.minWords, minWords || "?").replace(placeholders.maxWords, maxWords || "?");
};

/**
 * The prompt is stored per platform, but edited here in one place and saved to every platform
 * (BF-5072) — hence this screen and not the per-platform settings page. The ESB sends its defaults
 * along, so a field with no override is filled with the default rather than left grey: the admin
 * reads the wording the model actually gets. Saving a value that still equals the default clears the
 * override again, so the platform keeps following a later improvement.
 *
 * Only the editorial rules are editable. The text around them — the task, the language, the length,
 * the plain-text rule and the JSON contract — is shown read-only in the two blocks above and below,
 * so the admin edits a middle they can see the edges of instead of a loose fragment.
 */
const PromptSettingsModal = ({ isOpen, onClose }: IPromptSettingsModalProps): JSX.Element => {
  const intl = useIntl();

  const [settings, setSettings] = useState<ProductDescriptionSettingsDTO | null>(null);
  const [minWords, setMinWords] = useState("");
  const [maxWords, setMaxWords] = useState("");
  const [prompt, setPrompt] = useState("");
  const [errors, setErrors] = useState<IErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setErrors({});
    setIsLoading(true);

    // `cancelled` guards against setState after the modal closed while the request was in flight.
    const load = async () => {
      try {
        const loaded = await getDescriptionSettings();
        if (cancelled) {
          return;
        }
        const varying = loaded.varyingFields ?? [];
        setSettings(loaded);
        // Show the ESB default as a real value, so the admin reads what the model actually gets
        // instead of a grey placeholder. A field that varies across platforms stays empty — filling
        // it would hide the drift the warning is about.
        setMinWords(toInput(loaded.minWords ?? (varying.includes("minWords") ? null : loaded.defaultMinWords)));
        setMaxWords(toInput(loaded.maxWords ?? (varying.includes("maxWords") ? null : loaded.defaultMaxWords)));
        setPrompt(loaded.prompt ?? (varying.includes("prompt") ? "" : loaded.defaultPrompt ?? ""));
      } catch {
        if (!cancelled) {
          toast.error(intl.formatMessage({ id: "productDescription.settings.loadError" }));
          onClose();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Empty unless the platforms drifted apart — by hand in the database, or a save that only got
  // halfway. Those fields come back empty, so the form says so instead of showing one platform's value.
  const varyingFields = settings?.varyingFields ?? [];

  // An empty field means "use the ESB default", so that is the number the fixed block has to show.
  const effectiveMinWords = minWords.trim() || toInput(settings?.defaultMinWords);
  const effectiveMaxWords = maxWords.trim() || toInput(settings?.defaultMaxWords);

  const validate = (): IErrors => {
    const lower = settings?.wordsLowerBound ?? 0;
    const upper = settings?.wordsUpperBound ?? Number.MAX_SAFE_INTEGER;
    const promptLimit = settings?.promptMaxLength ?? Number.MAX_SAFE_INTEGER;
    const found: IErrors = {};

    // The ESB owns the bounds and mirrors them read-only, so this form cannot drift from the API.
    (
      [
        ["minWords", minWords],
        ["maxWords", maxWords]
      ] as Array<[keyof IErrors, string]>
    ).forEach(([field, raw]) => {
      const input = raw.trim();
      if (!input) {
        return;
      }
      const parsed = Number(input);
      if (!Number.isInteger(parsed) || parsed < lower || parsed > upper) {
        found[field] = intl.formatMessage({ id: "productDescription.settings.errors.wordCount" }, { lower, upper });
      }
    });

    // Compare the values the ESB will actually use: an empty field falls back to the default, so
    // "min 200, max empty" would otherwise ask the model for "200-120 words".
    const effectiveMin = minWords.trim() ? Number(minWords.trim()) : settings?.defaultMinWords;
    const effectiveMax = maxWords.trim() ? Number(maxWords.trim()) : settings?.defaultMaxWords;
    if (effectiveMin && effectiveMax && effectiveMin >= effectiveMax) {
      found.maxWords = intl.formatMessage({ id: "productDescription.settings.errors.wordCountOrder" });
    }

    if (prompt.length > promptLimit) {
      found.prompt = intl.formatMessage({ id: "productDescription.settings.errors.promptTooLong" }, { promptLimit });
    }

    return found;
  };

  const onSave = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      // Empty clears the override. So does a value equal to the default: storing it would freeze
      // this platform on today's wording and cut it off from a later improvement.
      const asNumber = (raw: string, fallback?: number): number | null => {
        const input = raw.trim();
        if (!input || Number(input) === fallback) {
          return null;
        }
        return Number(input);
      };

      await saveDescriptionSettings({
        minWords: asNumber(minWords, settings?.defaultMinWords),
        maxWords: asNumber(maxWords, settings?.defaultMaxWords),
        prompt: !prompt.trim() || prompt.trim() === (settings?.defaultPrompt ?? "").trim() ? null : prompt.trim()
      });
      toast.success(intl.formatMessage({ id: "productDescription.settings.saved" }));
      onClose();
    } catch {
      toast.error(intl.formatMessage({ id: "productDescription.settings.saveError" }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalComponent
      title={intl.formatMessage({ id: "productDescription.settings.title" })}
      isModalOpen={isOpen}
      onCloseModal={onClose}
      variant="big"
    >
      {isLoading ? (
        <SpinningLoader />
      ) : (
        <div className={styles.body}>
          <p className={styles.intro}>{intl.formatMessage({ id: "productDescription.settings.intro" })}</p>

          {varyingFields.length > 0 && (
            <p className={styles.varies}>
              {intl.formatMessage(
                { id: "productDescription.settings.varies" },
                {
                  fields: varyingFields
                    .map((field) => intl.formatMessage({ id: `productDescription.settings.${field}.label` }))
                    .join(", ")
                }
              )}
            </p>
          )}

          <div className={styles.wordRow}>
            <TextFieldComponent
              height={40}
              value={minWords}
              onChange={(event) => setMinWords(event.target.value)}
              placeholder={toInput(settings?.defaultMinWords)}
              errorMessage={errors.minWords}
              id="minWords"
              label={{
                label: intl.formatMessage({ id: "productDescription.settings.minWords.label" }),
                tooltip: intl.formatMessage({ id: "productDescription.settings.minWords.tooltip" })
              }}
            />
            <TextFieldComponent
              height={40}
              value={maxWords}
              onChange={(event) => setMaxWords(event.target.value)}
              placeholder={toInput(settings?.defaultMaxWords)}
              errorMessage={errors.maxWords}
              id="maxWords"
              label={{
                label: intl.formatMessage({ id: "productDescription.settings.maxWords.label" }),
                tooltip: intl.formatMessage({ id: "productDescription.settings.maxWords.tooltip" })
              }}
            />
          </div>

          {/* The three blocks in the order the model reads them: fixed, editable, fixed. An ESB that
              does not send the fixed text yet leaves them out instead of showing an empty box. */}
          {settings?.fixedPromptIntro && (
            <TextFieldComponent
              isTextArea
              fullWidth
              readOnly
              className={styles.fixedField}
              height={150}
              value={fillWordCounts(
                settings.fixedPromptIntro,
                settings.promptPlaceholders,
                effectiveMinWords,
                effectiveMaxWords
              )}
              id="fixedPromptIntro"
              label={{
                label: intl.formatMessage({ id: "productDescription.settings.fixedIntro.label" }),
                tooltip: intl.formatMessage({ id: "productDescription.settings.fixed.tooltip" })
              }}
            />
          )}

          <div>
            <TextFieldComponent
              isTextArea
              fullWidth
              height={220}
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={settings?.defaultPrompt}
              errorMessage={errors.prompt}
              id="prompt"
              label={{
                label: intl.formatMessage({ id: "productDescription.settings.prompt.label" }),
                tooltip: intl.formatMessage({ id: "productDescription.settings.prompt.tooltip" })
              }}
            />
            <p className={styles.hint}>{intl.formatMessage({ id: "productDescription.settings.prompt.hint" })}</p>
          </div>

          {settings?.fixedPromptOutro && (
            <div>
              <TextFieldComponent
                isTextArea
                fullWidth
                readOnly
                className={styles.fixedField}
                height={150}
                value={settings.fixedPromptOutro}
                id="fixedPromptOutro"
                label={{
                  label: intl.formatMessage({ id: "productDescription.settings.fixedOutro.label" }),
                  tooltip: intl.formatMessage({ id: "productDescription.settings.fixed.tooltip" })
                }}
              />
              <p className={styles.hint}>{intl.formatMessage({ id: "productDescription.settings.fixed.hint" })}</p>
            </div>
          )}

          <div className={styles.actions}>
            <ClickableComponent
              buttonType="button"
              variant="secondary"
              title={intl.formatMessage({ id: "productDescription.settings.cancel" })}
              height={48}
              onClick={onClose}
            />
            <ClickableComponent
              buttonType="button"
              title={intl.formatMessage({ id: "productDescription.settings.save" })}
              height={48}
              disabled={isSaving}
              onClick={onSave}
            />
          </div>
        </div>
      )}
    </ModalComponent>
  );
};

export { PromptSettingsModal };
